import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { getPostcodeValidationError } from "../../../../utils";
import { meterpointService } from "../../../../services/meterpoint";
import { trackEvent } from "../../../../utils/matomoAnalytics";
import { HelpCircle } from "lucide-react";
import styles from "./PostcodeInput.module.css";

const PostcodeInput = ({
  postcode,
  onPostcodeChange,
  onLookup,
  selectedFuel,
  skipAutoLookup = false,
}) => {
  const [validationError, setValidationError] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const handleSubmit = React.useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const error = getPostcodeValidationError(postcode);
      if (error) {
        setValidationError(error);
        return;
      }

      trackEvent("Address Lookup", "Click", selectedFuel, null);

      setValidationError("");
      if (onLookup) {
        onLookup([], true);
      }

      try {
        let results = await meterpointService.lookupByPostcode(
          postcode,
          selectedFuel,
        );

        if (onLookup) {
          onLookup(results, false);
        }
        trackEvent(
          "Address Lookup",
          "Success",
          selectedFuel,
          results?.length || 0,
        );
      } catch (error) {
        if (onLookup) {
          onLookup([], false);
        }

        // Track failed lookup
        trackEvent("Address Lookup", "Error", selectedFuel, null);
      }
    },
    [postcode, selectedFuel, onLookup],
  );

  useEffect(() => {
    if (
      postcode &&
      postcode.trim() !== "" &&
      !validationError &&
      !skipAutoLookup
    ) {
      handleSubmit(new Event("submit"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFuel]);

  const handleInputChange = (value) => {
    onPostcodeChange(value);

    // Validate immediately on every change
    const error = getPostcodeValidationError(value);
    setValidationError(error || "");
  };

  const toggleHelp = () => {
    setShowHelp(!showHelp);
    // Announce help toggle to screen readers
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", "polite");
    announcement.className = styles.srAnnouncement;
    announcement.textContent = showHelp
      ? "Help information hidden"
      : "Help information shown";
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  };

  return (
    <section className="mb-6" aria-labelledby="postcode-heading">
      <div className="mb-3">
        <h3
          id="postcode-heading"
          className="text-[15px] font-bold text-[var(--color-text-brand)] "
        >
          What is your business site postcode?
        </h3>
      </div>

      <div role="search" aria-labelledby="postcode-heading">
        <div className="flex gap-[15px] mb-4 max-md:gap-2">
          <div className="max-md:flex-1">
            <label htmlFor="postcode-input" className="sr-only ">
              Enter your business site postcode
            </label>
            <input
              id="postcode-input"
              type="text"
              value={postcode}
              onChange={(e) => handleInputChange(e.target.value)}
              className={`w-[215.48px] h-[43px] px-2.5 py-1.5 border rounded text-[13px] outline-none transition-all duration-200 bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] max-md:w-full max-md:text-xs max-[480px]:text-[11px] max-[480px]:px-2 max-[480px]:py-1.5 focus:border-[var(--color-border-focus)] focus:shadow-[var(--focus-box-shadow)] ${validationError ? "border-[#ef4600]" : "border-[var(--color-border-primary)]"}`}
              aria-invalid={!!validationError}
              aria-describedby={
                validationError ? "postcode-error" : "postcode-help"
              }
              autoComplete="postal-code"
            />
          </div>
          <div className="flex gap-2 items-center max-md:flex-1 max-md:gap-3">
            <button
              type="button"
              id="mtm-address-lookup"
              onClick={handleSubmit}
              className="w-[230.63px] h-[41.98px] flex items-center gap-1.5 font-medium text-center cursor-pointer border-none whitespace-nowrap px-3 py-1.5 text-[17px] leading-[1.4] bg-[var(--color-success)] text-white rounded-[1.2rem] tracking-wide uppercase max-md:w-full max-md:text-sm max-md:font-medium max-[480px]:text-xs max-[480px]:px-2.5 max-[480px]:py-1.5 hover:bg-[var(--color-success-hover)]  justify-center"
              aria-describedby="lookup-button-help"
              disabled={!postcode.trim()}
            >
              <span>Look Up</span>
              <svg
                className="w-4 h-4 max-[480px]:w-3 max-[480px]:h-3"
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            className="flex items-start justify-start bg-transparent border-none text-[var(--color-text-muted)] cursor-pointer ml-[-9px] mt-[-3px] w-5 h-5"
            onClick={toggleHelp}
            aria-label={`${showHelp ? "Hide" : "Show"} postcode help information`}
            aria-expanded={showHelp}
            aria-controls="postcode-help-content"
          >
            <HelpCircle className={styles.muiHelpIcon} aria-hidden="true" />
          </button>
        </div>

        <div id="postcode-help" className="sr-only">
          Enter your business site postcode to find available addresses
        </div>

        <div id="lookup-button-help" className="sr-only">
          Click to search for addresses at the entered postcode
        </div>

        {validationError && (
          <div
            id="postcode-error"
            className="text-[var(--color-danger)] text-sm -mt-2 font-light min-h-5"
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            {validationError}
          </div>
        )}
        {showHelp && (
          <section
            id="postcode-help-content"
            className="bg-[var(--color-warning-light)]  rounded-lg p-4 text-sm text-[#8a6d3b] leading-6 mt-2 animate-slideIn max-[480px]:p-3 max-[480px]:text-xs"
            aria-labelledby="postcode-help-heading"
          >
            <h4 id="postcode-help-heading" className="sr-only">
              Postcode Help Information
            </h4>
            Submitting an address search allows ENGIE to check your meter
            details against industry data.
          </section>
        )}
      </div>
    </section>
  );
};

PostcodeInput.propTypes = {
  postcode: PropTypes.string.isRequired,
  onPostcodeChange: PropTypes.func.isRequired,
  onLookup: PropTypes.func,
  selectedFuel: PropTypes.string.isRequired,
  skipAutoLookup: PropTypes.bool,
};

export default PostcodeInput;
